import { createAppKit } from '@reown/appkit/react'
import { WagmiProvider, http, fallback } from 'wagmi'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { CHAIN_ID, APP_NETWORK, RPC_URLS } from '../utils/contractAddresses'

const queryClient = new QueryClient()

const projectId = process.env.REACT_APP_REOWN_PROJECT_ID

const metadata = {
  name: 'FRDM Finance',
  description: 'FRDM Finance - Decentralized Finance Platform',
  url: process.env.REACT_APP_REOWN_METADATA_URL,
  icons: ['https://avatars.githubusercontent.com/u/179229932']
}

console.log("process.env.REACT_APP_CHAIN", process.env.REACT_APP_CHAIN)
console.log("react app metadata url", process.env.REACT_APP_REOWN_METADATA_URL)

// utils/contractAddresses.js resolves which chain this build talks to, and
// refuses to load if REACT_APP_CHAIN and REACT_APP_CHAIN_ID disagree. Everything
// — the modal, the chain guard, the read hooks — reads it from there.
const networks = [APP_NETWORK]

/**
 * Drop AppKit's remembered network BEFORE it can hydrate from it.
 *
 * AppKit persists the last active network as `eip155:<id>`. On boot,
 * `CaipNetworkUtil.getCaipNetworkFromStorage()` looks that id up in
 * `ChainController.getAllRequestedCaipNetworks()` and, on a miss, substitutes an
 * "unsupported network" placeholder whose `id` is a STRING
 * (`caipNetworkId.split(':')[1]`). Everything then goes wrong at once:
 *
 *   - `syncAccount()` early-returns on an unsupported network, BEFORE the branch
 *     that opens the switch-network modal — which is why the prompt that used to
 *     appear "disappeared".
 *   - the WalletConnect connector reports that string id, and wagmi compares it
 *     to the numeric connection id with `!==`, so every signature dies with
 *     "current chain (id: X) does not match the connection's chain (id: X)" —
 *     the same number printed twice.
 *
 * UNCONDITIONAL, and that matters. Only clearing ids that differ from ours was
 * not enough: on a real phone AppKit produced the placeholder for `eip155:11155111`
 * on a Sepolia build — its OWN chain — because that lookup runs before the
 * network list is populated. The result was a banner insisting the wallet was on
 * the wrong network while naming the right one, and a login that could not
 * proceed. This app has exactly one network, so there is nothing worth
 * remembering: clear the key and let `defaultNetwork` below decide, every time.
 */
function clearRememberedNetwork() {
  try {
    window.localStorage.removeItem('@appkit/active_caip_network_id')
  } catch (e) {
    // Safari private mode throws on localStorage access. Nothing to repair.
  }
}

clearRememberedNetwork()

/**
 * Every in-app chain READ goes through this transport: the pre-send checks
 * before a fee, allowance reads, and the receipt waits that chain an approve
 * into its follow-up transaction. The wallet still signs and broadcasts.
 *
 * Without it AppKit defaults to fallback([Reown RPC, chain's public default]).
 * On Sepolia that pair failed ~55% of reads on 2026-10-04 — Reown answers
 * "chain is not available on free plan" intermittently and sepolia.drpc.org
 * answers it every time — so the appeal screen refused a valid appeal as
 * "could not verify on-chain". Polygon's default (polygon-rpc.com) returns 401.
 *
 * Order: the REACT_APP_RPC_URLS list (free public endpoints) in the order
 * given, then Reown last — AppKit appends its Reown entry behind any transport
 * passed here. Each endpoint gets RPC_TIMEOUT_MS so one that hangs is skipped
 * quickly instead of freezing a payment screen for viem's default 10s.
 *
 * `rank` health-checks every endpoint once a minute (one eth_blockNumber each)
 * and moves a dead or hanging one to the back. Without it a hanging FIRST
 * endpoint cost every single read the full timeout, because a fallback always
 * starts at the top. Healthy endpoints are ordered by stability and latency,
 * so the env order is the starting order, not a fixed one.
 */
const RPC_TIMEOUT_MS = 5000
const RPC_RANK_INTERVAL_MS = 60_000
const transports = RPC_URLS.length
  ? {
      [CHAIN_ID]: fallback(
        RPC_URLS.map((url) => http(url, { timeout: RPC_TIMEOUT_MS })),
        // Health-check timeout matches the request timeout: viem's 1s default
        // marked a healthy-but-slower endpoint (Tenderly) as failed in Chrome.
        { rank: { interval: RPC_RANK_INTERVAL_MS, timeout: RPC_TIMEOUT_MS } }
      ),
    }
  : undefined

// 4. Create Wagmi Adapter
const wagmiAdapter = new WagmiAdapter({
  networks,
  projectId,
  transports,
  ssr: true
})

// 5. Create modal
createAppKit({
  adapters: [wagmiAdapter],
  networks,
  projectId,
  metadata,
  themeMode: "light",
  // Stated rather than inherited from AppKit's defaults: this app talks to
  // exactly one chain (CHAIN_ID), so a wallet on anything else must be
  // switched, never tolerated.
  defaultNetwork: APP_NETWORK,
  allowUnsupportedChain: false,
  enableNetworkSwitch: true,
  features: {
    analytics: true,
    email: false,
    socials: ['google', 'apple']
  }
})

console.log("app chain id", CHAIN_ID)

export function AppKitProvider({ children }) {
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </WagmiProvider>
  )
}
